var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useMemo, useState } from 'react';
import { Button, InlineNotification, SelectInput, Text } from '@ui';
import { MissingEquipmentTypes } from './MissingEquipmentTypes';
import { ignoreEquipmentType, saveEquipmentTypeMap } from '../shared/api';
import { translatorFor } from '../shared/i18n';
import { humanize } from '../shared/types';
const t = translatorFor('setup_results');
export function EquipmentMatch({ data, urls, equipmentTypeMap, deviceTypeOptions, onEquipmentTypeMapChange, onEquipmentTypeIgnored }) {
    const [savingKind, setSavingKind] = useState(null);
    const [ignoringId, setIgnoringId] = useState(null);
    const [ignoredIds, setIgnoredIds] = useState([]);
    const [error, setError] = useState(null);
    const mappedKinds = useMemo(() => new Set(equipmentTypeMap.map((row) => row.ipSystemDeviceType)), [equipmentTypeMap]);
    const mappedStIds = useMemo(() => new Set(equipmentTypeMap.map((row) => row.stEquipmentTypeId)), [equipmentTypeMap]);
    const unmappedKinds = deviceTypeOptions.filter((kind) => !mappedKinds.has(kind));
    const availableNames = data.catalog
        .filter((type) => !mappedStIds.has(type.id))
        .map((type) => ({ value: type.id, label: type.name }));
    const needsMatch = [...data.unmatched, ...data.ambiguous].filter((type) => !ignoredIds.includes(type.id));
    const mapKind = (kind, stEquipmentTypeId) => __awaiter(this, void 0, void 0, function* () {
        const stType = data.catalog.find((type) => type.id === stEquipmentTypeId);
        if (!stType)
            return;
        setSavingKind(kind);
        setError(null);
        try {
            const saved = yield saveEquipmentTypeMap(urls, [
                ...equipmentTypeMap,
                { ipSystemDeviceType: kind, ipSystemDevice: null, stEquipmentTypeId: stType.id, stEquipmentType: stType.name }
            ]);
            onEquipmentTypeMapChange(saved);
        }
        catch (_a) {
            setError(t('map_failed'));
        }
        finally {
            setSavingKind(null);
        }
    });
    const ignore = (type) => __awaiter(this, void 0, void 0, function* () {
        setIgnoringId(type.id);
        setError(null);
        try {
            const ignored = { stEquipmentTypeId: type.id, stEquipmentType: type.name };
            yield ignoreEquipmentType(urls, ignored);
            onEquipmentTypeIgnored(ignored);
            setIgnoredIds((previous) => [...previous, type.id]);
        }
        catch (_a) {
            setError(t('ignore_failed'));
        }
        finally {
            setIgnoringId(null);
        }
    });
    return (React.createElement("div", { className: "service-titan-settings__problem-body" },
        React.createElement(Text, { variant: "body", as: "p" }, t('equipment_summary', { matched: data.matched, needsMatch: needsMatch.length })),
        unmappedKinds.length > 0 && availableNames.length > 0 && (React.createElement("div", { className: "service-titan-settings__match-rows" }, unmappedKinds.map((kind) => (React.createElement(SelectInput, { key: kind, id: `service-titan-match-${kind}`, label: humanize(kind), options: availableNames, disabled: savingKind !== null, showSearch: true, onChange: (value) => {
                void mapKind(kind, String(value));
            } }))))),
        needsMatch.length > 0 && (React.createElement("ul", { className: "service-titan-settings__plain-list" }, needsMatch.map((type) => (React.createElement("li", { key: type.id, className: "service-titan-settings__list-row" },
            React.createElement(Text, { variant: "body", as: "span" }, type.name),
            React.createElement(Button, { variant: "tertiary", disabled: ignoringId === type.id, onClick: () => {
                    void ignore(type);
                } }, t('ignore'))))))),
        React.createElement(MissingEquipmentTypes, { catalog: data.catalog, urls: urls, onEquipmentTypeMapChange: onEquipmentTypeMapChange }),
        error && React.createElement(InlineNotification, { variant: "error" }, error)));
}
export default EquipmentMatch;
